import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { CompaniesService } from 'src/app/services/companies.service';
import { TicketType } from '../enums/ticketing.enum';
import { ICreateTicket } from '../models/ITicket.interface';
import { ConsoleCaptureService } from './console-capture.service';
import { ScreenshotService } from './screenshot.service';
import { TicketingApiService } from './ticketing-api.service';
import { TicketsStore } from './tickets.store';

export interface IReportFlowOptions {
  type: TicketType;
  route: string;

  /** The widget's own elements, hidden while the page is rasterised. */
  hideDuringCapture: HTMLElement[];
}

/** What the modal is seeded with, and what it hands back. */
export interface ITicketDraft {
  type: TicketType;
  title: string;
  description: string;
  stepsToReproduce: string;
  expected: string;
  actual: string;
  reporterPriority: number;
  module: string | null;
  route: string | null;

  /** Null once the reporter removes it, or when capture failed. */
  screenshot: Blob | null;

  files: File[];

  /** False once the reporter removes the "Included automatically" block. */
  includeContext: boolean;
}

/**
 * Owns the reporting flow: capture, annotate, describe, submit.
 *
 * Lives in a service rather than the widget so the same flow can start from the "New ticket" button on
 * the Tickets page, where there is no launcher to hang it off.
 *
 * The draft is held here and survives the modal closing, so a mis-click does not throw away a typed
 * description — in memory only, for the session, as the design specifies.
 */
@Injectable({ providedIn: 'root' })
export class TicketReportFlowService {
  private readonly dialog = inject(MatDialog);
  private readonly screenshots = inject(ScreenshotService);
  private readonly capture = inject(ConsoleCaptureService);
  private readonly api = inject(TicketingApiService);
  private readonly store = inject(TicketsStore);
  private readonly companies = inject(CompaniesService);
  private readonly snackBar = inject(MatSnackBar);

  /** How long the capture is waited for before the flow moves on without it. */
  private static readonly CAPTURE_TIMEOUT_MS = 8000;

  /** Survives the modal closing so an accidental dismiss does not lose what was typed. */
  private draft = signal<ITicketDraft | null>(null);

  async start(options: IReportFlowOptions): Promise<void> {
    // Said straight away, before any rasterising: pressing the launcher and watching nothing happen
    // for several seconds reads as a dead button. Dismissed the moment the editor or the form opens.
    const preparing = this.snackBar.open('Preparing your report…', undefined, { duration: 30000 });
    try {
      await this.runStart(options, () => preparing.dismiss());
    } finally {
      preparing.dismiss();
    }
  }

  private async runStart(options: IReportFlowOptions, ready: () => void): Promise<void> {
    const existing = this.draft();

    // Resume rather than restart when there is a draft of the same type. Someone who closed the modal
    // by accident gets their text back; someone switching bug→feature gets a clean form.
    let draft: ITicketDraft = existing && existing.type === options.type
      ? existing
      : this.blankDraft(options);

    // Bugs default to a screenshot, feature requests do not — a feature request is usually about
    // something that is not on screen.
    if (!draft.screenshot && options.type === TicketType.Bug) {
      draft = { ...draft, screenshot: await this.tryCapture(options.hideDuringCapture) };
    }

    if (draft.screenshot) {
      ready();
      const annotated = await this.openEditor(draft.screenshot);

      // undefined = cancelled out of the editor entirely; null = "Skip screenshot".
      if (annotated === undefined) {
        this.draft.set(draft);
        return;
      }
      draft = { ...draft, screenshot: annotated };
    }

    this.draft.set(draft);
    ready();
    await this.openModal();
  }

  /** Opened directly from Global Requests → Tickets → New ticket, with no capture step. */
  async startWithoutCapture(type: TicketType, route: string): Promise<void> {
    const existing = this.draft();
    this.draft.set(
      existing && existing.type === type
        ? existing
        : this.blankDraft({ type, route, hideDuringCapture: [] }),
    );
    await this.openModal();
  }

  /**
   * A capture failure must not stop a bug being reported. The screenshot is an aid; the description is
   * the ticket.
   */
  private async tryCapture(hide: HTMLElement[]): Promise<Blob | null> {
    // Capped, because a capture that takes longer than this is one the reporter has already given up
    // on — and the describe step is what actually files the ticket. The capture is left to finish or
    // fail on its own; only the waiting stops.
    const timeout = new Promise<'timeout'>(resolve => setTimeout(() => resolve("timeout"), TicketReportFlowService.CAPTURE_TIMEOUT_MS));

    try {
      const result = await Promise.race([this.screenshots.captureViewport(hide), timeout]);
      if (result === 'timeout') {
        this.snackBar.open('The screenshot is taking too long, so it has been skipped. You can still describe the problem.', 'OK', { duration: 6000 });
        return null;
      }
      return result;
    } catch (error) {
      // Logged as well as announced. The reporter is told something kind and useless; this is the
      // only place the actual reason survives, and a screenshot that fails for everyone is a bug we
      // would otherwise only hear about as "it doesn't work".
      console.error('[bct] Screenshot capture failed.', error);
      this.snackBar.open('The screenshot could not be captured. You can still describe the problem.', 'OK', { duration: 5000 });
      return null;
    }
  }

  private async openEditor(screenshot: Blob): Promise<Blob | null | undefined> {
    const { ScreenshotEditorComponent } = await import(
      '../screenshot-editor/screenshot-editor.component'
    );

    const ref = this.dialog.open(ScreenshotEditorComponent, {
      data: { screenshot },
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      panelClass: 'bct-editor-dialog',
      // The editor is the whole screen and its own Esc handling asks before discarding annotations.
      disableClose: true,
      autoFocus: false,
    });

    return firstValueFrom(ref.afterClosed());
  }

  private async openModal(): Promise<void> {
    const { TicketModalComponent } = await import('../ticket-modal/ticket-modal.component');

    const ref = this.dialog.open(TicketModalComponent, {
      data: { draft: this.draft() },
      width: '720px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    });

    const result = await firstValueFrom(ref.afterClosed());

    if (!result) {
      // Dismissed. Keep whatever the modal handed back through onDraftChange so reopening restores it.
      return;
    }

    await this.submit(result as ITicketDraft);
  }

  /** Called by the modal as fields change, so a dismissal does not lose the typing. */
  rememberDraft(draft: ITicketDraft): void {
    this.draft.set(draft);
  }

  private async submit(draft: ITicketDraft): Promise<void> {
    const payload: ICreateTicket = {
      type: draft.type,
      title: draft.title.trim(),
      description: draft.description.trim(),
      stepsToReproduce: draft.type === TicketType.Bug ? draft.stepsToReproduce || null : null,
      expected: draft.type === TicketType.Bug ? draft.expected || null : null,
      actual: draft.type === TicketType.Bug ? draft.actual || null : null,
      reporterPriority: draft.reporterPriority,
      module: draft.module,
      route: draft.route,
      companyId: this.companies.managedCompanyId,

      // Omitted entirely when declined — an empty object would still record a context row.
      context: draft.includeContext
        ? {
            appVersion: this.appVersion(),
            browser: navigator.userAgent,
            os: navigator.platform,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            consoleErrorsJson: this.capture.errorsJson(),
            failedRequestsJson: this.capture.requestsJson(),
          }
        : null,
    };

    try {
      const created = await firstValueFrom(
        this.api.create(payload, draft.screenshot, draft.files),
      );

      // Cleared only on success, so a failed submit keeps everything the reporter typed.
      this.draft.set(null);

      this.snackBar.open(
        `Ticket ${created.key} submitted. You can follow it in Global Requests → Tickets.`,
        'OK',
        { duration: 6000 },
      );
      this.store.refreshSummary();
    } catch {
      // Not "saved to drafts": the draft is this service's own, in memory, and reopening New ticket
      // restores it. Global Requests > Drafts is a different thing entirely (request drafts), and
      // sending a reporter there to find a ticket they will never see is worse than saying nothing.
      this.snackBar.open(
        'The ticket could not be submitted. What you typed is still in the New ticket form — reopen it to try again. It is lost if you reload the page.',
        'Dismiss',
        { duration: 9000 },
      );
    }
  }

  private blankDraft(options: IReportFlowOptions): ITicketDraft {
    return {
      type: options.type,
      title: '',
      description: '',
      stepsToReproduce: '',
      expected: '',
      actual: '',
      reporterPriority: 1,
      module: this.moduleFromRoute(options.route),
      route: options.route,
      screenshot: null,
      files: [],
      includeContext: true,
    };
  }

  /**
   * First path segment, title-cased — "/invoicing/quotations" becomes "Invoicing". A guess the
   * reporter can correct in the modal, which beats asking them which module they are in.
   */
  private moduleFromRoute(route: string): string | null {
    const segment = route.split('?')[0].split('/').filter(Boolean)[0];
    if (!segment) return null;
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  }

  private appVersion(): string {
    return environment.VERSION;
  }
}
